/**
 * Public profile username / handle rules.
 *
 * Mirrors AppBackend/libs/usernameValidation.js and
 * AppBackend/constants/usernameSettings.js — keep the two in sync. Client-side
 * validation is a preflight; the server always has the final say (see
 * checkUsernameAvailable() in src/api/auth.ts).
 *
 * Character set is Instagram-style: [A-Za-z0-9._], 3–30 chars, no leading /
 * trailing period, no two periods in a row. Uniqueness comparison is
 * case-insensitive but the display form preserves the case the user typed.
 */

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;

export const USERNAME_REGEX = /^[A-Za-z0-9._]+$/;

/** Case-insensitive; values here are already lowercase. */
export const RESERVED_USERNAMES: ReadonlySet<string> = new Set([
    // 1. under /profile/*
    'edit',
    'new',
    'create',
    'delete',

    // 2. top-level SPA routes
    'admin',
    'api',
    'auth',
    'book',
    'booking',
    'callback',
    'community',
    'destinations',
    'faq',
    'itineraries',
    'itinerary',
    'list',
    'lists',
    'login',
    'logout',
    'my-travel',
    'notifications',
    'onboarding',
    'performance',
    'place',
    'post',
    'profile',
    'recommendation',
    'redirect',
    'results',
    'saved',
    'search',
    'settings',
    's',
    'storefront',
    'travel-styles',
    'trips',
    'well-known',

    // 3. marketing / brand / meta
    'about',
    'advisors',
    'anonymous',
    'blog',
    'contact',
    'creators',
    'for-advisors',
    'for-creators',
    'globethrivers',
    'help',
    'me',
    'official',
    'privacy',
    'privacy-policy',
    'root',
    'security',
    'staff',
    'support',
    'system',
    'team',
    'terms',
    'terms-and-conditions',
    'user',
    'users',
    'you',
]);

export type UsernameFailureCode =
    | 'empty'
    | 'too_short'
    | 'too_long'
    | 'bad_chars'
    | 'bad_position'
    | 'reserved';

export type ValidateUsernameResult =
    | { ok: true; normalized: string }
    | { ok: false; code: UsernameFailureCode; message: string };

export function isReservedUsername(candidate: string): boolean {
    return RESERVED_USERNAMES.has(candidate.toLowerCase());
}

export function validateUsername(candidate: string | null | undefined): ValidateUsernameResult {
    if (candidate == null) {
        return { ok: false, code: 'empty', message: 'Please choose a username.' };
    }
    if (typeof candidate !== 'string') {
        return { ok: false, code: 'bad_chars', message: 'Username must be text.' };
    }
    const value = candidate.trim();
    if (value.length === 0) {
        return { ok: false, code: 'empty', message: 'Please choose a username.' };
    }
    if (value.length < USERNAME_MIN_LENGTH) {
        return {
            ok: false,
            code: 'too_short',
            message: `Username must be at least ${USERNAME_MIN_LENGTH} characters.`,
        };
    }
    if (value.length > USERNAME_MAX_LENGTH) {
        return {
            ok: false,
            code: 'too_long',
            message: `Username can't be longer than ${USERNAME_MAX_LENGTH} characters.`,
        };
    }
    if (!USERNAME_REGEX.test(value)) {
        return {
            ok: false,
            code: 'bad_chars',
            message: 'Only letters, numbers, periods, and underscores are allowed.',
        };
    }
    if (value.startsWith('.') || value.endsWith('.')) {
        return {
            ok: false,
            code: 'bad_position',
            message: "Username can't start or end with a period.",
        };
    }
    if (value.includes('..')) {
        return {
            ok: false,
            code: 'bad_position',
            message: "Username can't contain two periods in a row.",
        };
    }
    if (isReservedUsername(value)) {
        return {
            ok: false,
            code: 'reserved',
            message: 'That username is reserved. Try another.',
        };
    }
    return { ok: true, normalized: value };
}

/**
 * True when `s` looks like a Mongo ObjectId (24 hex chars). Used to dispatch
 * `/profile/:idOrUsername` routes between the id-form and the handle-form.
 */
export function isObjectId(s: string | null | undefined): boolean {
    return typeof s === 'string' && /^[a-f0-9]{24}$/i.test(s);
}

export interface ResolveProfileOwnerIdParams {
    /** `:id` from `/profile|/storefront|/book/:id` (ObjectId or username handle). */
    routeId?: string | null;
    /** Resolved owner `_id` from loaded profile data. */
    profileDataId?: string | null;
    /** Logged-in viewer's `_id` (for `/profile` with no route segment). */
    viewerUserId?: string | null;
}

/**
 * Mongo `_id` for profile APIs that reject username handles in the URL segment.
 * Prefer loaded `profileDataId`; use `routeId` when it is already an ObjectId;
 * use `viewerUserId` when there is no route id; return null for a handle until
 * profile data has loaded (do not fall back to the viewer on someone else's handle).
 */
export function resolveProfileOwnerId({
    routeId,
    profileDataId,
    viewerUserId,
}: ResolveProfileOwnerIdParams): string | null {
    if (profileDataId != null && profileDataId !== '') {
        return String(profileDataId);
    }

    const route = routeId != null && routeId !== '' ? String(routeId) : null;
    if (!route) {
        return viewerUserId != null && viewerUserId !== '' ? String(viewerUserId) : null;
    }

    if (isObjectId(route)) {
        return route;
    }

    return null;
}

/** True when the route `:id` segment refers to this profile (ObjectId or username). */
export function routeIdMatchesProfile(
    routeId: string | null | undefined,
    profile: { _id?: string | null; username?: string | null },
): boolean {
    if (routeId == null || routeId === '') return false;
    const route = String(routeId);
    if (profile._id != null && String(profile._id) === route) return true;
    if (profile.username != null && profile.username !== '' && profile.username === route) {
        return true;
    }
    return false;
}

/**
 * The three URL prefixes that render the ProfilePage. `/profile` is the legacy
 * canonical for travelers; `/storefront` is the term of art for creators and
 * advisors; `/book` is a brand alias for off-app marketing (business cards,
 * email signatures) and never serves as a canonical URL.
 */
export type ProfileRoutePrefix = '/profile' | '/storefront' | '/book';

export const PROFILE_ROUTE_PREFIXES: readonly ProfileRoutePrefix[] = [
    '/profile',
    '/storefront',
    '/book',
] as const;

/**
 * Pick the `ProfileRoutePrefix` corresponding to the current `location.pathname`,
 * so ProfilePage canonicalization (id → handle redirect) keeps the user on
 * whichever prefix they typed. Falls back to `/profile` for unrecognized paths.
 */
export function routePrefixForPathname(pathname: string | null | undefined): ProfileRoutePrefix {
    if (typeof pathname !== 'string') return '/profile';
    if (pathname.startsWith('/storefront')) return '/storefront';
    if (pathname.startsWith('/book')) return '/book';
    return '/profile';
}

/** Internal: resolve the `/<prefix>/<slug>` href for a user, slug = handle || _id. */
function buildHrefForPrefix(
    user: {
        _id?: string | { toString(): string } | null | undefined;
        username?: string | null | undefined;
    } | null | undefined,
    prefix: ProfileRoutePrefix,
): string {
    const handle = typeof user?.username === 'string' ? user.username.trim() : '';
    if (handle) return `${prefix}/${handle}`;
    const raw = user?._id;
    if (!raw) return prefix;
    const idStr = typeof raw === 'string' ? raw : raw.toString();
    return `${prefix}/${idStr}`;
}

/**
 * Legacy `/profile/<slug>` href. Still used by travelers' nav and anywhere the
 * caller specifically wants the profile prefix (not the brand-aligned storefront
 * form). New call sites should prefer `hrefForUserProfile` unless they have a
 * reason to pin the prefix.
 */
export function buildProfileHref(user: {
    _id?: string | { toString(): string } | null | undefined;
    username?: string | null | undefined;
}): string {
    return buildHrefForPrefix(user, '/profile');
}

/** `/storefront/<slug>` — brand prefix for creators and advisors. */
export function buildStorefrontHref(user: {
    _id?: string | { toString(): string } | null | undefined;
    username?: string | null | undefined;
}): string {
    return buildHrefForPrefix(user, '/storefront');
}

/** `/book/<slug>` — booking alias; same page content as the profile. */
export function buildBookHref(user: {
    _id?: string | { toString(): string } | null | undefined;
    username?: string | null | undefined;
}): string {
    return buildHrefForPrefix(user, '/book');
}

/**
 * The right public href to show for a user, given who they are: creators and
 * advisors go to `/storefront/<handle>` (that's the term of art they put on
 * their business cards and in their bios), and travelers keep `/profile/<handle>`.
 * `/book/<handle>` is intentionally never emitted here — it's reserved for
 * marketing contexts the user explicitly opts into.
 */
export function hrefForUserProfile(user: {
    _id?: string | { toString(): string } | null | undefined;
    username?: string | null | undefined;
    profile_type?: string | null | undefined;
}): string {
    const type = user?.profile_type;
    if (type === 'creator' || type === 'advisor') {
        return buildHrefForPrefix(user, '/storefront');
    }
    return buildHrefForPrefix(user, '/profile');
}
